import React from 'react';

import { ProButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import useFreeProTrial from '@hooks/useFreeProTrial';
import { ClickTarget, TrackingLocation, click } from '@tracking';

interface ConfirmationModalProps {
  closeModal: () => void;
  costChargedToday: number;
  isOpen: boolean;
  offerCode: string;
}

export const START_TRIAL_TEXT = 'Start free trial';

const ConfirmationModal = ({
  closeModal,
  costChargedToday,
  isOpen,
  offerCode,
}: ConfirmationModalProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Modal,
  });
  const { mutateAsync: handleFreeProTrial, isLoading } = useFreeProTrial();

  const handleStartTrial = async () => {
    await handleFreeProTrial({ offer_code: offerCode });
  };

  return (
    <TIModal
      isOpen={isOpen}
      onClose={closeModal}
      disableScrollLock={false}
      customHeader={true}
      isFullScreen={true}
      colorScheme={TiModalColorScheme.dark}
      parentClass="w-full max-w-full xs:max-w-sm sm:max-w-xl md:max-w-5xl lg:max-w-container-lg"
    >
      <div className="absolute right-8 top-8 flex justify-end sm:right-16 sm:top-16">
        <IconClose
          className="h-[26px] w-[26px] cursor-pointer text-white"
          onClick={closeModal}
          data-test="close-modal"
          aria-role="button"
          strokeWidth="1"
        />
      </div>
      <div
        className="mx-auto flex max-w-[632px] flex-col items-center justify-center p-24 text-center xs:p-48 md:px-0 md:py-56"
        data-testid="free-pro-trial-confirmation-modal"
      >
        <div className="font-suisse-intl-mono text-overline-lg font-normal text-pro-gold">
          Trial
        </div>
        <h2 className="m-0 font-suisse-works text-headline-sm text-white sm:text-headline-lg lg:text-headline-xl">
          The Information Pro
        </h2>
        <p className="mb-0 mt-16 font-suisse-intl-mono text-overline-lg font-normal text-white">
          We’re glad you decided to take advantage of our exclusive offer. Your subscription will
          continue without interruption with access to Pro for the next three months. You will be
          billed ${Number(costChargedToday / 100).toFixed(2)} today and your subscription will renew
          after 12 months unless cancelled.
        </p>
        <ProButton
          onClickCapture={() => {
            trackEvent(click({ clickTarget: ClickTarget.StartTrial, clickText: START_TRIAL_TEXT }));
          }}
          as="button"
          className="mt-32"
          size="big"
          onClick={handleStartTrial}
          disabled={isLoading}
          isLoading={isLoading}
        >
          {START_TRIAL_TEXT}
        </ProButton>
      </div>
    </TIModal>
  );
};

export default ConfirmationModal;
